import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  ComponentProps,
} from 'react';

import PropTypes from 'prop-types';

import { noop, useOnClickOutside } from '@adsk/alloy-react-helpers';
import { AlertInformationIcon, RenderIconType } from '@adsk/alloy-react-icon';
import theme, { StyleProp, stylePropType } from '@adsk/alloy-react-theme';

import Flyout from './Flyout';

export type InfoPopoverProps = Omit<
  React.ComponentProps<typeof Flyout>,
  'content'
> & {
  renderIcon?: RenderIconType;
  size?: number;
  message: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  shouldOpenOnClick?: boolean;
  overlayStyle?: StyleProp;
};

const InfoPopover = React.forwardRef<HTMLDivElement, InfoPopoverProps>(
  (
    {
      className,
      style,
      renderIcon = AlertInformationIcon.renderIcon,
      size = 16,
      placement = Flyout.PLACEMENTS.BOTTOM,
      message,
      container,
      shouldOpenOnClick = true,
      onClick = noop,
      variant,
      overlayStyle,
      delayHide,
      delayShow,
      onShow: onShowProp,
      onHide: onHideProp,
      ...props
    },
    ref,
  ) => {
    const overlayRef = useRef<HTMLElement>(null);
    const targetRef = useRef<SVGSVGElement>(null);
    const [isClicked, setIsClicked] = useState(false);
    const [isMouseOver, setIsMouseOver] = useState(false);
    const isOpen = isClicked || isMouseOver;
    const timeoutHideRef = useRef<NodeJS.Timeout | null>(null);
    const timeoutShowRef = useRef<NodeJS.Timeout | null>(null);

    const clearShowAndHideTimeouts = useCallback(() => {
      clearTimeout(timeoutHideRef.current ?? undefined);
      clearTimeout(timeoutShowRef.current ?? undefined);
    }, []);

    useEffect(
      () => () => clearShowAndHideTimeouts(),
      [clearShowAndHideTimeouts],
    );

    function handleClickOutside() {
      clearShowAndHideTimeouts();

      timeoutHideRef.current = setTimeout(() => {
        setIsClicked(false);
        setIsMouseOver(false);
      }, delayHide);
    }

    function onClickClose(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
      if (!isClicked && shouldOpenOnClick) {
        clearShowAndHideTimeouts();

        timeoutShowRef.current = setTimeout(() => {
          setIsClicked(true);
        }, delayShow);
      }

      onClick?.(e);
    }

    useOnClickOutside(
      Boolean(shouldOpenOnClick && isClicked),
      handleClickOutside,
      [targetRef, overlayRef],
    );

    const onShow = () => {
      onShowProp?.();
      setIsMouseOver(true);
    };

    const onHide = () => {
      onHideProp?.();
      setIsMouseOver(false);
    };

    const renderFlyoutBody = useCallback(
      (props: ComponentProps<typeof Flyout.Body>) => {
        const variantContainerPadding =
          variant === Flyout.VARIANTS.DARK ? '8px 12px' : '16px';

        return <Flyout.Body {...props} padding={variantContainerPadding} />;
      },
      [variant],
    );

    return (
      // Will be fixed in https://jira.autodesk.com/browse/DSGN-1836
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div ref={ref} className={className} css={[style]} onClick={onClickClose}>
        <Flyout
          overlayRef={overlayRef}
          content={message}
          show={isOpen}
          container={container}
          renderBody={renderFlyoutBody}
          placement={placement}
          delayHide={delayHide || 100}
          delayShow={delayShow}
          variant={variant}
          style={overlayStyle}
          onShow={onShow}
          onHide={onHide}
          trigger={Flyout.TRIGGER_EVENTS.HOVER}
          {...props}
        >
          {renderIcon?.({
            ref: targetRef,
            size,
            style: {
              cursor: 'pointer',
              outline: 'none',
              color: theme.tokens.colors.icon.information.value,
            },
            'aria-hidden': false,
          })}
        </Flyout>
      </div>
    );
  },
);

InfoPopover.displayName = 'InfoPopover';

InfoPopover.propTypes = {
  /**
   * className to add to the InfoPopover component
   */
  className: PropTypes.string,
  /**
   * style to add to the InfoPopover component
   */
  style: stylePropType,
  /**
   * The icon square size
   */
  size: PropTypes.number,
  /**
   * A node to be shown on hover.
   */
  message: PropTypes.any.isRequired,
  /**
   * Optional function to run when the info icon is pressed. Passes the click event target
   */
  onClick: PropTypes.func,
  /**
   * Optional parameter to specify if InfoPopover will stay opened on click.
   */
  shouldOpenOnClick: PropTypes.bool,
  /**
   * The icon to be displayed for the info
   */
  renderIcon: PropTypes.func,
  /**
   * The icon to be displayed for the info
   */
  placement: PropTypes.oneOf(Object.values(Flyout.PLACEMENTS)),
  /**
   * InfoPopover variant
   */
  variant: PropTypes.oneOf(Object.values(Flyout.VARIANTS)),
  /**
   * delay before show in milliseconds
   */
  delayShow: PropTypes.number,
  /**
   * delay before hide in milliseconds
   */
  delayHide: PropTypes.number,
  /**
   * style to add to the Overlay component
   */
  overlayStyle: stylePropType,
  /**
   * callback to be called on show
   */
  onShow: PropTypes.func,
  /**
   * callback to be called on hide
   */
  onHide: PropTypes.func,
};

export default Object.assign(InfoPopover, {
  Arrow: Flyout.Arrow,
  Target: Flyout.Target,
  Body: Flyout.Body,
  VARIANTS: Flyout.VARIANTS,
  PLACEMENTS: Flyout.PLACEMENTS,
  TRIGGER_EVENTS: Flyout.TRIGGER_EVENTS,
  TRIGGER_TARGETS: Flyout.TRIGGER_TARGETS,
});
